import { GetServerSideProps } from 'next'
import numeral from 'numeral'
import Link from 'next/link'
import { slugify } from 'utils/slug'
import api from 'services/api'
import Layout from 'components/layout'
import JsonLd, { CommonJsonLd } from 'components/json-ld'
import Cover from 'components/cover'
import FoldingText from 'components/folding-text'
import TitleBar from 'components/title-bar'
import SellingPoint from 'components/selling-point'
import routes from 'routes'
import BookDetails from 'models/book-details'
import Comment from 'models/comment'
import ChapterListItem from 'models/chapter-list-item'
import ChapterBlockList from 'components/chapter-block-list'
import SoftLink from 'components/soft-link'
import { NextSeo } from 'next-seo'
import Avatar from 'components/avatar'
import { StoryContext } from 'contexts/story'
import { useContext, useEffect, useState } from 'react'
import { getRedirectReturn } from 'utils/redirect'
import { HOST } from 'config'
import Keywords from 'components/keywords'
import Book from 'models/book'
import Breadcrumb from 'components/breadcrumb'
import AppDownload from 'components/app-download'
import hotWords from 'data/hotWords.json'
import AppLink from 'components/app-link'
import Whatsapp from 'components/whatsapp'
import { withStorage } from 'utils/withFunction'
import { formatUtcString } from 'utils/date'
import Author from 'models/author'
import Toast from 'components/Toast'

function copyToClipboard(text) {
  const textarea = document.createElement('textarea')
  textarea.value = text
  document.body.appendChild(textarea)
  textarea.select()
  document.execCommand('copy')
  document.body.removeChild(textarea)
}

export const Story = ({
  bookInfo,
  chapters,
  path,
  comments,
  descriptionTemp,
  userInfo,
}: {
  bookInfo: BookDetails
  chapters: ChapterListItem[]
  path: string
  comments: Comment
  descriptionTemp: string
  userInfo: string
}): JSX.Element => {
  const [userProfile] = useState(userInfo)
  const { book, author, tags, sellingPoints, statistics } = bookInfo
  const [bookshelfList, setBookshelfList] = useState([])

  const storyContext = useContext(StoryContext)

  useEffect(() => {
    storyContext.currentStory = book

    return () => {
      storyContext.currentStory = null
    }
  })

  const url = HOST + routes.story.details({ id: book.id, title: book.title })

  const description = book.completed
    ? `Read ${book.title} by ${author.name} on Anystories. ${
        tags.length > 0
          ? `This novel is blong to ${tags[0].name} story${
              tags.length > 1
                ? `, covering ${tags
                    .slice(1, 4)
                    .map((item) => item.name)
                    .join(', ')}.`
                : '.'
            }`
          : ''
      } 50,000+ Best Romance Web Novel & Fantasy Fiction for you to explore.`
    : `Read ${book.title} by ${author.name} on Anystories. The ${
        book.title
      } Story and Novel is being serialized to ${chapters.length} - ${
        chapters[chapters.length - 1].title
      }. ${
        tags.length > 0
          ? `This novel is blong to ${tags[0].name} story${
              tags.length > 1
                ? `, covering ${tags
                    .slice(1, 4)
                    .map((item) => item.name)
                    .join(', ')}...`
                : '.'
            }`
          : ''
      }`

  // const login = () => {
  //   router.push(`/login?returnUrl=/stories/${book.id}/${slugify(book.title)}`)
  // }

  const getBookshelf = () => {
    api.bookshelf.getBookshelf().then((response) => {
      setBookshelfList(response.userBookshelfList)
    })
  }

  // const onBookshelfToggle = async () => {
  //   if (bookshelfList.some((item) => item.id === book.id)) {
  //     return false
  //   }
  //   if (!userProfile) {
  //     login()
  //   } else {
  //     Loading.show()
  //     await api.bookshelf.putBookshelf({
  //       books: [
  //         {
  //           id: book.id,
  //           added: new Date().toISOString() + '',
  //         },
  //       ],
  //     })
  //     Loading.hide()
  //     getBookshelf()
  //   }
  // }

  useEffect(() => {
    if (userProfile) {
      getBookshelf()
    }
  }, [userProfile])

  return (
    <Layout
      showDownloadBtn={false}
      isFullScreen={true}
      showBottomDownloadPanel={true}
    >
      <meta itemProp="bookFormat" content="EBook" />
      <Keywords
        keywords={[
          book.title,
          author.name,
          'Romance Story',
          ...(tags || []).map((tag) => tag.name + ' Story'),
        ]}
      />
      <NextSeo
        title={`${book.title} Story & Novel PDF Read Online for Free - AnyStories`}
        description={description}
        canonical={`${HOST}${path}`}
        openGraph={{
          title: book.title,
          description: book.description,
          url,
          type: 'book',
          images: [
            {
              url: book.cover,
              alt: book.title,
            },
          ],
        }}
      />
      <CommonJsonLd />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'AnyStories',
              item: HOST,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: book.title,
              item: `${HOST}${path}`,
            },
          ],
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'Book',
          mainEntityOfPage: `${HOST}${path}`,
          headline: book.title,
          name: book.title,
          genre: tags?.[0]?.name || '',
          image: {
            '@type': 'ImageObject',
            url: book.cover,
            height: 197,
            width: 148,
          },
          bookFormat: 'https://schema.org/EBook',
          // datePublished: '2020-04-17T14:30:09.000Z（书籍发布时间）',
          // dateModified: '2023-09-06T16:02:32.000Z（书籍更新时间）',
          author: {
            '@type': 'Person',
            name: author.name,
          },
          // copyrightHolder: '版权所有方',
          publisher: {
            '@type': 'Organization',
            name: 'AnyStories',
            logo: {
              '@type': 'ImageObject',
              url: 'https://m.anystories.app/images/logo-anystories.png',
            },
          },
          description: book.description,
          aggregateRating: {
            '@type': 'AggregateRating',
            bestRating: '5.0',
            ratingValue: (Math.random() * (4.9 - 4.5) + 4.5).toFixed(1),
            ratingCount: (879 + (statistics.addBookshelf || 0) / 10).toFixed(0),
          },
          review: comments.list.map((item) => {
            return {
              '@type': 'Review',
              author: {
                '@type': 'Person',
                name: item.user.name,
              },
              datePublished: item.ctime,
              reviewBody: item.content,
              reviewRating: {
                '@type': 'Rating',
                ratingValue: (Math.random() * (4.9 - 4.5) + 4.5).toFixed(1),
              },
            }
          }),
        }}
      />
      <div
        itemScope
        itemType="https://schema.org/Book"
        itemID={
          'https://m.anystories.app' +
          routes.story.details({ id: book.id, title: bookInfo.book.title })
        }
      >
        <div className="relative overflow-hidden lg:-mt-2">
          <div
            className="absolute w-[150%] h-[150%] -top-[25%] -left-[25%] bg-cover bg-no-repeat bg-center"
            style={{
              backgroundImage: `url(${book.cover})`,
              filter: 'blur(32px)',
            }}
          ></div>
          <div className="relative bg-[#333333] bg-opacity-[0.15] z-10">
            <div className="max-w-[1440px] mx-auto pb-8 lg:pb-4">
              <TitleBar
                className="text-white"
                // right={
                //   <div className="h-full">
                //     <AppLink>
                //       <section className="relative flex items-center justify-center bg-black bg-opacity-[0.1] p-1 rounded">
                //         <div className="flex flex-1 text-[#ffffff] font-medium text-xs leading-tight line-clamp-2 transform origin-left scale-90">
                //           <p>
                //             Download
                //             {' <'}
                //             <b>
                //               {book.title.length > 30
                //                 ? book.title.substr(0, 30) + '...'
                //                 : book.title}
                //             </b>
                //             {'> '} for free!
                //           </p>
                //         </div>
                //         <div className="flex items-center justify-center w-24 h-6 bg-[#15141F] text-[#FEDD00] rounded font-bold text-xs drop-shadow-lg">
                //           DOWNLOAD
                //         </div>
                //       </section>
                //     </AppLink>
                //     {/* <span
                //       className="text-white"
                //       onClick={() => {
                //         onBookshelfToggle()
                //       }}
                //     >
                //       {!bookshelfList.some((item) => item.id === book.id) ? (
                //         <svg
                //           className="w-6 h-6"
                //           fill="none"
                //           stroke="currentColor"
                //           strokeWidth={1.5}
                //           viewBox="0 0 24 24"
                //           xmlns="http://www.w3.org/2000/svg"
                //           aria-hidden="true"
                //         >
                //           <path
                //             strokeLinecap="round"
                //             strokeLinejoin="round"
                //             d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"
                //           />
                //         </svg>
                //       ) : (
                //         <svg
                //           className="w-6 h-6"
                //           fill="currentColor"
                //           viewBox="0 0 24 24"
                //           xmlns="http://www.w3.org/2000/svg"
                //           aria-hidden="true"
                //         >
                //           <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
                //         </svg>
                //       )}
                //     </span> */}
                //   </div>
                // }
                rightClass="w-auto"
              />
              <div className="hidden lg:block px-4 py-3 text-white">
                <Breadcrumb
                  className="mr-4"
                  items={[
                    {
                      name: 'Stories',
                      link: '/',
                    },
                    {
                      name: bookInfo.book.title,
                    },
                  ]}
                />
              </div>
              <div className="relative flex px-4 pt-2 pb-4">
                <div className="w-[100px] lg:w-[148px] flex-none mr-4">
                  <Cover book={book} />
                </div>
                <div className="flex flex-col pt-1">
                  {sellingPoints && sellingPoints.length > 0 && (
                    <div className="mb-3 text-goldenrod">
                      <SellingPoint text={sellingPoints[0]} />
                    </div>
                  )}
                  <h1
                    className="font-bold mb-3 text-white lg:text-2xl"
                    itemProp="name"
                  >
                    {book.title}
                  </h1>
                  <h2 className="text-xs mb-3 text-white opacity-50">
                    Author: {author.name}
                  </h2>
                  <div className="flex-grow lg:hidden"></div>
                  <div className="text-xs text-white">
                    {book.words
                      ? numeral(book.words ?? 0).format('0,0.0a') + ' Words / '
                      : ''}
                    {book.completed !== false ? 'Completed' : 'Ongoing'}{' '}
                  </div>
                  <div className="hidden lg:block lg:flex-grow"></div>
                  <div className="hidden lg:flex text-white">
                    <Number name="Hot" number={statistics.hot} />
                    <Number name="Views" number={statistics.views} />
                  </div>
                </div>
                <Link
                  href={routes.story.chapter({
                    bookId: book.id,
                    bookTitle: book.title,
                    chapterOrdinal: bookshelfList.some(
                      (item) => item.id === book.id
                    )
                      ? bookshelfList.find((item) => item.id === book.id)
                          .order || 1
                      : 1,
                  })}
                  className="hidden lg:flex items-center justify-center absolute w-60 h-12 bottom-28 left-2/3 right-4 z-10 rounded-full bg-gold text-goldInnerText font-medium cursor-pointer"
                >
                  Start Reading
                </Link>
                {/* <div
                  className={`hidden lg:flex items-center justify-center absolute w-60 h-12 bottom-24 left-2/3 right-4 z-10 rounded-full border border-solid border-white font-medium text-white ${
                    bookshelfList.some((item) => item.id === book.id)
                      ? 'cursor-pointer opacity-30'
                      : ''
                  }`}
                  onClick={() => {
                    onBookshelfToggle()
                  }}
                >
                  {bookshelfList.some((item) => item.id === book.id)
                    ? 'ADDED'
                    : 'Add to Library'}
                </div> */}
                <div className="hidden lg:flex items-center absolute h-12 bottom-4 left-2/3">
                  <div className="flex items-center gap-x-2">
                    <span className="text-white">Share:</span>
                    <a
                      href={`https://www.facebook.com/sharer.php?u=${HOST}${path}&t=${book.title}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img
                        src="/images/share-fb.svg"
                        className="w-10 h-10"
                        alt="facebook"
                      />
                    </a>
                    <a
                      href={`https://twitter.com/share/?url=${HOST}${path}&text=${book.title}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img
                        src="/images/share-twitter.svg"
                        className="w-10 h-10"
                        alt="twitter"
                      />
                    </a>
                    <a
                      href={`https://pinterest.com/pin/create/button/?url=${HOST}${path}&media=${book.cover}&description=${book.title}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img
                        src="/images/share-pinterest.svg"
                        className="w-10 h-10"
                        alt="pinterest"
                      />
                    </a>
                    <a
                      href={`https://api.whatsapp.com/send?text=${book.title}, ${HOST}${path}&via=lopscoop`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img
                        src="/images/share-whatsapp.svg"
                        className="w-10 h-10"
                        alt="whatsapp"
                      />
                    </a>
                    <a
                      href={`https://www.reddit.com/submit?url=${HOST}${path}&title=${book.title}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <img
                        src="/images/share-reddit.svg"
                        className="w-10 h-10"
                        alt="reddit"
                      />
                    </a>
                    <span
                      className="flex items-center justify-center bg-gray-300 rounded-full w-10 h-10"
                      onClick={() => {
                        copyToClipboard(`${HOST}${path}`)
                        Toast.show({
                          content: 'The link has been pasted to your clipboard',
                          delay: 1000,
                        })
                      }}
                    >
                      <svg
                        className="w-6 h-6 text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.5}
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"
                        />
                      </svg>
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between w-full h-12 px-4 lg:hidden">
                <Whatsapp location={'bookdetail'}>
                  <div className="flex flex-[1] items-center rounded-full px-4 h-10 border border-solid border-white text-white">
                    <img
                      src="/images/icon-union.png"
                      className="w-4 h-4 mr-1 text-sm"
                      alt="Read with Bonus"
                    />
                    Read with Bonus
                  </div>
                </Whatsapp>
                <div className="flex flex-[2] pl-4 lg:absolute lg:w-60 lg:bottom-28 lg:left-2/3">
                  <Link
                    href={routes.story.chapter({
                      bookId: book.id,
                      bookTitle: book.title,
                      chapterOrdinal: bookshelfList.some(
                        (item) => item.id === book.id
                      )
                        ? bookshelfList.find((item) => item.id === book.id)
                            .order
                        : 1,
                    })}
                    className="flex items-center justify-center w-full bg-gold text-goldInnerText h-10 px-4 rounded-full text-center font-bold shadow-lg"
                  >
                    Start Reading
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="relative -mt-4 flex justify-around pt-3 rounded-t-2xl bg-white lg:hidden z-10">
        <Number name="Hot" number={statistics.hot} />
        <Number name="Views" number={statistics.views} />
      </div>
      <div className="max-w-[1440px] mx-auto">
        <section className="desktop:flex">
          <section className="desktop:w-[1018px] mb-4">
            <div className="flex">
              <div className="w-full lg:flex-[3]">
                <AppLink>
                  <section className="relative flex items-center m-4 mt-8 px-4 py-3 rounded-lg bg-gradient-to-r from-[#d9d6ff] to-[#ffd3d4]">
                    <div className="w-16 h-14 mr-1">
                      <div className="absolute -top-4 -left-0 w-24 h-24 bg-appDownloadBookCover bg-100%"></div>
                    </div>
                    <div className="flex flex-1 mx-2 text-[#35343F] font-medium leading-5 text-xs lg:text-base line-clamp-3">
                      <p>
                        Download {chapters.length} Chapters of
                        {' <'}
                        <span className="text-[#FA3830]">{book.title}</span>
                        {'> '} in App
                      </p>
                    </div>
                    <div className="relative flex items-center justify-center w-24 h-7 bg-[#15141F] text-[#FEDD00] rounded font-bold text-xs drop-shadow-lg">
                      DOWNLOAD
                      <span className="absolute -top-3 -right-1 flex items-center justify-center py-1 px-1.5 h-4 rounded-full rounded-bl-none bg-red text-white text-xs">
                        <i className="transform scale-90">FREE</i>
                      </span>
                    </div>
                  </section>
                </AppLink>
              </div>
              <div className="hidden relative lg:flex lg:flex-[1.5] items-center m-4 mt-8 p-4 rounded-lg bg-[#E1F7CA]">
                <Whatsapp location="bookdetail">
                  <span className="text-[#35343F] font-medium">
                    Follow our{' '}
                    <i className="text-[#017963] font-bold">WhatsApp</i> and
                    unlock
                    <br /> an enhanced reading experience :-)
                  </span>
                  <img
                    src="/images/icon-union.png"
                    className="absolute -top-4 right-2 w-8 h-8"
                    alt="Join the group"
                  />
                </Whatsapp>
              </div>
            </div>
            {book.description && (
              <div className="mb-4 p-4">
                <h2 className="font-bold text-xl mb-2">Introduction</h2>
                {tags && (
                  <div className="mb-2">
                    <ul className="flex flex-wrap text-sm">
                      {tags.map((tag) => (
                        <li
                          key={tag.id}
                          className="block mr-2 mb-2 rounded bg-gold bg-opacity-50 text-goldInnerText"
                        >
                          <Link
                            href={routes.tag.bookList({
                              id: tag.id,
                              title: tag.name,
                            })}
                            title={tag.name}
                            className="block px-2 py-1 font-medium"
                          >
                            {tag.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <FoldingText
                  text={descriptionTemp}
                  className="text-[#15141F] text-sm"
                  itemProp="descriptionTemp"
                />
              </div>
            )}
            <div className="mb-4 px-4 lg:hidden">
              <h2 className="font-bold text-xl mb-2">Share the book to</h2>
              <div className="flex items-center gap-x-2">
                <a
                  href={`https://www.facebook.com/sharer.php?u=${HOST}${path}&t=${book.title}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src="/images/share-fb.svg"
                    className="w-10 h-10"
                    alt="facebook"
                  />
                </a>
                <a
                  href={`https://twitter.com/share/?url=${HOST}${path}&text=${book.title}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src="/images/share-twitter.svg"
                    className="w-10 h-10"
                    alt="twitter"
                  />
                </a>
                <a
                  href={`https://pinterest.com/pin/create/button/?url=${HOST}${path}&media=${book.cover}&description=${book.title}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src="/images/share-pinterest.svg"
                    className="w-10 h-10"
                    alt="pinterest"
                  />
                </a>
                <a
                  href={`https://api.whatsapp.com/send?text=${book.title}, ${HOST}${path}&via=lopscoop`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src="/images/share-whatsapp.svg"
                    className="w-10 h-10"
                    alt="whatsapp"
                  />
                </a>
                <a
                  href={`https://www.reddit.com/submit?url=${HOST}${path}&title=${book.title}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src="/images/share-reddit.svg"
                    className="w-10 h-10"
                    alt="reddit"
                  />
                </a>
                <span
                  className="flex items-center justify-center bg-gray-300 rounded-full w-10 h-10"
                  onClick={() => {
                    copyToClipboard(`${HOST}${path}`)
                    Toast.show({
                      content: 'The link has been pasted to your clipboard',
                      delay: 1000,
                    })
                  }}
                >
                  <svg
                    className="w-6 h-6 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.5}
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"
                    />
                  </svg>
                </span>
              </div>
            </div>
            <section className="mb-4 p-4 lg:hidden">
              <h2 className="font-bold mb-2 text-xl">About Author</h2>
              <AuthorBlock author={author} />
            </section>
            <div className="mb-4 p-4">
              <div className="flex justify-between items-center mb-3">
                <h2 className="font-bold text-xl">Latest Chapters</h2>
                <div className="text-xs text-gray-400">
                  <Link
                    href={routes.story.chapterList({
                      id: book.id,
                      title: book.title,
                    })}
                  >
                    All chapters
                    <svg
                      className="h-4 inline-block"
                      xmlns="https://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </Link>
                </div>
              </div>
              <ChapterBlockList
                chapters={[...chapters].reverse().slice(0, 10)}
                book={book}
              />
            </div>
            <div className="mb-4 p-4">
              <Comments book={book} comments={comments} />
            </div>
          </section>
          <section className="desktop:w-[422px]">
            <section className="hidden lg:block p-4 mb-4">
              <h2 className="font-bold mb-2 text-xl">About Author</h2>
              <AuthorBlock author={author} />
            </section>
            {/* {recommendedStories?.length > 0 && statistics.hot < 1000 && (
              <div className="p-4 mb-4">
                <h3 className="font-bold mb-2 text-xl">Recommend</h3>
                <div className="p-4 bg-gray-100 rounded">
                  <BookView
                    book={recommendedStories[0]}
                    tagsWhite={true}
                    showAuthor={false}
                  />
                  <div className="mt-4 flex space-x-2">
                    <Link
                      href="/"
                      className="block bg-white py-2 px-6 rounded text-center font-bold"
                    >
                      More
                    </Link>
                    <Link
                      href={routes.story.details(recommendedStories[0].book)}
                      className="block flex-grow text-gold bg-black py-2 px-4 rounded text-center font-bold"
                    >
                      Read Now
                    </Link>
                  </div>
                </div>
              </div>
            )} */}
            <section className="px-4 p-4 mb-4">
              <AppDownload />
            </section>
          </section>
        </section>
      </div>
    </Layout>
  )
}

const Number = ({ name, number }: { name: string; number: number }) => {
  return (
    <div className="flex flex-col lg:flex-row items-center lg:mr-10">
      <div className="mr-2 font-bold text-xl">
        {numeral(number ?? 0).format('0,0.[0]a')}
      </div>
      <div className="text-xs text-gray-400">{name}</div>
    </div>
  )
}

const AuthorBlock = ({ author }: { author: Author }) => {
  const link = routes.author.details(author)

  return (
    <SoftLink href={link}>
      <div className="flex items-center bg-gray-100 rounded-lg p-4">
        <Avatar
          src={author.photo}
          name={author.name}
          className="w-10 h-10 mr-2"
        />
        <div>
          <div
            className="font-bold mb-1 leading-4"
            itemProp="author"
            itemScope
            itemType="https://schema.org/Person"
            itemID={'https://m.anystories.app' + routes.author.details(author)}
          >
            <Link href={routes.author.details(author)} title={author.name}>
              {author.name}
            </Link>
          </div>
          {/* <div className="text-xs text-gray-500">
            {pluralize('Story', author.stories, true)}
          </div> */}
        </div>
        <div className="flex-grow"></div>
        <div className="lg:hidden">
          <svg
            xmlns="https://www.w3.org/2000/svg"
            className="w-6 stroke-current text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </div>
      </div>
    </SoftLink>
  )
}

const Comments = ({ book, comments }: { book: Book; comments: Comment }) => {
  return (
    <>
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-bold text-xl">Comments</h3>
        {comments.list.length > 0 ? (
          <div className="text-xs text-gray-400">
            <Link
              href={routes.comment.list({
                id: book.id,
              })}
            >
              All Comments
              <svg
                className="h-4 inline-block"
                xmlns="https://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </Link>
          </div>
        ) : null}
      </div>
      {comments.list.length > 0 ? (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {comments.list.map((item) => {
            return item.user ? (
              <li className="flex items-start mb-3" key={item.id}>
                <div className="flex-none w-18">
                  <Avatar src={item.user.avatar} className="w-12 h-12 mr-4" />
                </div>
                <div className="flex flex-col">
                  <h4 className="text-sm font-bold mb-1">{item.user.name}</h4>
                  <p className="text-xs mb-1 break-all">{item.content}</p>
                  <div className="text-xs text-gray-400">
                    {formatUtcString(item.ctime)}
                  </div>
                </div>
              </li>
            ) : null
          })}
        </ul>
      ) : (
        <div className="py-4 text-xs text-gray-300 text-center">
          No comments yet.
        </div>
      )}
    </>
  )
}

export const getServerSideProps: GetServerSideProps = withStorage(
  async ({ context }) => {
    const id = context.params.id as string
    const slug = context.params.slug as string
    // const userInfo = context.req.cookies['user-info'] || null
    const userInfo = null

    // 临时302跳转
    if (id === '63ee41fd6252850013672388') {
      return {
        redirect: {
          statusCode: 302,
          destination: `/stories/64e3192f9101612cf3b6c059/true-luna`,
        },
      }
    }

    // eslint-disable-next-line
    let [bookInfo, chapters, comments] = await Promise.all([
      api.book.getBookInfo(id),
      api.chapter.getChapterList(id),
      api.comment.getComments({ id }),
    ])

    if (!bookInfo) {
      return {
        redirect: {
          permanent: false,
          destination: `/search?keywords=${slug.replaceAll('-', '+')}`,
        },
      }
    }

    const path = routes.story.details({ id, title: bookInfo.book.title })

    if (slugify(bookInfo.book.title) !== slug) {
      return getRedirectReturn(context.req.url, path)
    }

    let descriptionTemp = bookInfo.book.description
    descriptionTemp = descriptionTemp.replace(
      new RegExp(hotWords.join('|'), 'g'),
      function (match) {
        return `<a href="/search?keywords=${match
          .trim()
          .replaceAll(
            ' ',
            '+'
          )}" target="_blank" class="font-bold hover:bg-goldenrod hover:bg-opacity-20 border-b border-dashed border-gray-400">${match}</a>`
      }
    )

    return {
      props: {
        bookInfo,
        chapters,
        path,
        comments,
        descriptionTemp,
        userInfo,
      },
    }
  }
)

export default Story
